import { useQueryClient } from '@tanstack/react-query';
import type { AuthResult, LoginInput, Role, SessionUser } from '@trafiku/contracts';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, onUnauthorized, refreshSession, setAccessToken } from './api';

interface AuthState {
  user: SessionUser | null;
  ready: boolean;
  login(input: LoginInput): Promise<void>;
  logout(): Promise<void>;
  can(...roles: Role[]): boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    onUnauthorized(() => {
      setAccessToken(null);
      setUser(null);
    });
    refreshSession().then((result) => {
      setUser(result?.user ?? null);
      setReady(true);
    });
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const result = await api<AuthResult>('/auth/login', { method: 'POST', body: input });
    setAccessToken(result.accessToken);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    setAccessToken(null);
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ user, ready, login, logout, can: (...roles) => !!user && roles.includes(user.role) }),
    [user, ready, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
